<template>
    <div v-if="isVisible" v-click-outside="close" :class="['user', { [`theme-${layout.theme}`]: true, open: isOpen, }]">
        <button
            class="ds-btn ds-btn--secondary btn-user"
            @click="loggedIn ? isOpen = !isOpen : navigateTo(loginUrlWithCallBack, {external: true})"
        >
            <slot name="user-icon">
                <DSIcon class="user-icon" code="default-line/user" size="xl" />
            </slot>
            <span>{{ loggedIn ? firstname : $t('login.link') }}</span>
            <DSIcon
                v-if="loggedIn"
                class="chevron"
                code="default-fill/chevron-down"
                size="xs"
            />
        </button>
        <div class="options" :inert="!isOpen">
            <nuxt-link :to="`${$paths.getPath('my-pages')}/kayttaja/asetukset`" class="option">
                {{ $t('login.userDetails') }}
            </nuxt-link>
            <button class="option" @click="userStore.logout() && close()">
                {{ $t('login.logout') }}
            </button>
        </div>
    </div>
</template>
<script setup>
import { ref, inject, navigateTo, computed, useI18n, useNuxtApp, storeToRefs } from '#imports';
import vClickOutside from '~dsc/directives/click-outside';
import { useUserStore } from '~dsc/stores/cms/user';
const userStore = useUserStore();
const { loggedIn, user } = storeToRefs(userStore);
const { firstname } = user.value;
const layout = inject('layout', { theme: 'white' });
const isOpen = ref(false);
const close = () => {
    isOpen.value = false;
};

const props = defineProps({
    customerType: {
        type: String,
        required: false,
        default: 'B2C',
        validator: value => [ 'B2C', 'B2B' ].includes(value),
    },
    isVisible: {
        type: Boolean,
        required: false,
        default: true,
    },
});
const { $paths } = useNuxtApp();
const loginUrlWithCallBack = computed(() => `${$paths.getPath('account-hub')}?callbackURL=${$paths.getPath('login-callback-url')}&service=${props.customerType}`);

defineOptions({
    inheritAttrs: false,
});

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            login: {
                link: 'Kirjaudu',
                userDetails: 'Käyttäjätiedot',
                logout: 'Kirjaudu ulos',
            },
        },
        'en-EU': {
            login: {
                link: 'Login',
                userDetails: 'User Details',
                logout: 'Logout',
            },
        },
        'sv-SE': {
            login: {
                link: 'Mina sidor',
                userDetails: 'Användare',
                logout: 'Logga ut',
            },
        },
        'no-NO': {
            login: {
                link: 'Logg inn',
                userDetails: 'Bruker',
                logout: 'Logge ut',
            },
        },
    },
});
</script>
<style scoped>
.user {
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  position: relative;
}
.user.theme-black .label {
  color: var(--color-base-white);
}
.user.theme-black .option {
  color: var(--color-base-white);
}
.user.theme-black .option.selected {
  color: var(--color-secondary-500);
  border-bottom: 0.25rem solid var(--color-secondary-500);
}
.user.theme-clear .ds-btn.ds-btn--secondary {
  border: 2px solid var(--color-base-white);
}
@media (max-width: 1199px) {
  .user.theme-clear .ds-btn.ds-btn--secondary {
    border-color: var(--color-base-black);
  }
}
.user .chevron {
  transition: all 0.2s ease-in-out;
  position: relative;
  margin-left: 0.5rem;
  transform: rotate(0) translateY(0);
  transform-origin: center center;
}
.user .label {
  display: inline-flex;
  align-items: center;
  padding: 0;
  min-height: 1.5rem;
  justify-content: flex-end;
  font-weight: 700;
}
.user .label:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}
.user .ds-btn {
  align-items: center;
}
.user .options {
  transition: all 0.2s ease-in-out;
  position: absolute;
  bottom: -0.5rem;
  right: 0;
  display: flex;
  flex-flow: column wrap;
  transform: translateY(100%) scaleY(0);
  transform-origin: center top;
  background: var(--color-base-white);
  border-radius: 0.25rem;
  padding: 1rem;
  box-shadow: var(--panel-box-shadow);
  z-index: 1;
}
.user .options .option {
  transition: all 0.2s ease-in-out;
  line-height: 1.5em;
  font-size: 0.875rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  letter-spacing: 0.025em;
  font-weight: 700;
  color: var(--color-base-grey-600);
  text-align: left;
}
.user .options .option.selected, .user .options .option:hover {
  color: var(--color-base-black);
}
.user .options .option.selected {
  pointer-events: none;
  border-bottom: 0.25rem solid var(--color-primary-500);
}
.user .options .option:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}
.user .options .option:last-child {
  margin-bottom: 0;
}
.user .icon.icon-user {
  margin-right: 0.5rem;
}
.user.open .chevron {
  transform: rotate(-180deg) translateY(2px);
}
.user.open .options {
  transform: translateY(100%) scaleY(1);
}
@media (max-width: 1199px) {
  .user .label {
    margin: 0;
    padding: 0;
  }
  .user .label :nth-child(2) {
    display: none;
  }
  .user .icon.chevron {
    display: none;
  }
}
</style>
